import { AddIcon } from "@chakra-ui/icons";
import { Box, Button, Flex, Heading, Text } from "@chakra-ui/react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Explanation } from "../../common/support/Explanation/Explanation";

/**
 * Where the listed requirements come from. SourceMagnet is an organization like any other, so its
 * admins see both an org section (their own, private to them) and the global standard — the subtitle
 * is what tells the two apart, and the org one names the organization to leave no doubt which is which.
 */
type Source = { scope: "org"; orgName: string } | { scope: "global"; orgName?: never };

type Props = Source & {
  title: ReactNode;
  editable: boolean;
  addRefusal?: string;
  addLabel: ReactNode;
  onAdd: () => void;
  /**
   * An empty section says so in one quiet line rather than in the list's full empty state: the page
   * stacks up to six sections, and the heading already carries the only "add" the library needs.
   */
  isEmpty: boolean;
  emptyText: ReactNode;
  children: ReactNode;
};

export const LibrarySourceSection = ({
  title,
  scope,
  orgName,
  editable,
  addRefusal,
  addLabel,
  onAdd,
  isEmpty,
  emptyText,
  children,
}: Props) => {
  const { t } = useTranslation();

  return (
    <Box>
      <Flex justify="space-between" align="center" mb="1" gap="3">
        <Heading as="h3" size="sm" color="smText">
          {title}
        </Heading>
        {editable && (
          <Explanation text={addRefusal} enabled={!!addRefusal} shouldWrapChildren>
            <Button
              size="sm"
              colorScheme="teal"
              leftIcon={<AddIcon />}
              onClick={onAdd}
              flexShrink={0}
              isDisabled={!!addRefusal}
            >
              {addLabel}
            </Button>
          </Explanation>
        )}
      </Flex>
      <Text fontSize="sm" color="smMuted" mb="3">
        {scope === "org"
          ? t("Owned by {{org}}. Visible only to your organization.", { org: orgName })
          : t("The SourceMagnet standard, shared with every organization on the platform.")}
        {scope === "global" && editable && ` ${t("Changes here apply to every organization.")}`}
        {editable && addRefusal && ` ${addRefusal}`}
      </Text>
      {isEmpty ? (
        <Text fontSize="sm" color="smMuted" fontStyle="italic">
          {emptyText}
        </Text>
      ) : (
        children
      )}
    </Box>
  );
};
